import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import CohortDetailsComponent from "./component";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router-dom";

const CohortViewContainer = (props) => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [cohortDetailsData, setCohortDetailsData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [shouldResetFields, setShouldResetFields] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Load Cohort Details
  const LoadCohortViewDetails = () => {
    setLoading(true);
    props.getDataFromAPI(
      `/dashboard/api/v2/admin/vendingmachines/vm-cohorts/${id}`,
      "GET",
      undefined,
      (response) => {
        setCohortDetailsData(response);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading cohort:", error);
        setLoading(false);
        toastr.error("Failed to load cohort details");
      },
      true
    );
  };

  // Function to download CSV directly from response
  const downloadCSVFile = (csvString, filename) => {
    if (!csvString) {
      toastr.error("No data to download");
      return false;
    }

    try {
      // Add BOM for UTF-8 to handle special characters
      const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toastr.success("File downloaded successfully");
      return true;
    } catch (error) {
      console.error("Download error:", error);
      toastr.error("Failed to download file");
      return false;
    }
  };

  // Download VM List API - Button click handler
  const handleDownloadVMList = () => {
    if (isDownloading) {
      toastr.warning("Download already in progress");
      return;
    }
    
    setIsDownloading(true);
    
    props.getDataFromAPI(
      `/partner/api/v2/cohort/${id}/download-vm-list`,
      "GET",
      undefined,
      (response) => {
        setIsDownloading(false);
        
        console.log("API Response:", response);
        
        if (response) {
          const fileName = `cohort_vm_list_${id}.csv`;
          let csvContent = '';
          
          // Check if response is already a string (CSV format)
          if (typeof response === 'string') {
            csvContent = response;
          }
          // Check if response has data property that is a string
          else if (response && response && typeof response === 'string') {
            csvContent = response;
          }
          // Check if response is an array
          else if (Array.isArray(response) && response.length > 0) {
            // Convert array to CSV
            const headers = Object.keys(response[0]);
            csvContent = headers.join(',') + '\n';
            for (const row of response) {
              const values = headers.map(header => {
                const val = row[header] || '';
                return typeof val === 'string' && (val.includes(',') || val.includes('"')) 
                  ? `"${val.replace(/"/g, '""')}"` 
                  : val;
              });
              csvContent += values.join(',') + '\n';
            }
          }
          else {
            toastr.warning("No data available to download");
            return;
          }
          
          if (csvContent) {
            downloadCSVFile(csvContent, fileName);
          } else {
            toastr.warning("No content to download");
          }
        } else {
          toastr.warning("No data available to download");
        }
      },
      (error) => {
        console.error("Download API Error:", error);
        setIsDownloading(false);
        toastr.error("Failed to download VM list", error?.message || "Please try again");
      },
      true
    );
  };

  useEffect(() => {
    LoadCohortViewDetails();
  }, []);

  const handleDeleteClick = (userId) => {
    setDeleteType("user");
    setDeleteIds([userId]);
    setDeleteMessage("Are you sure you want to remove this user?");
    setShowDeleteModal(true);
  };

  const handleVmDeleteClick = (vmIds) => {
    setDeleteType("vm");
    let ids = Array.isArray(vmIds) ? vmIds : [vmIds];
    ids = ids.map(id => id.toString());
    setDeleteIds(ids);
    
    if (ids.length === 1) {
      setDeleteMessage(`Are you sure you want to remove VM ID: ${ids[0]}?`);
    } else {
      setDeleteMessage(`Are you sure you want to remove ${ids.length} vending machine(s)?`);
    }
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    setDeleting(true);
    if (deleteType === "user") {
      CohortUserRemove(deleteIds[0]);
    } else if (deleteType === "vm") {
      CohortBatchVMRemove(deleteIds);
    }
  };

  const CohortUserRemove = (userId) => {
    props.getDataFromAPI(
      `/dashboard/api/v2/admin/vendingmachines/vm-cohorts/${id}/users/${userId}`,
      "DELETE",
      undefined,
      () => {
        toastr.success("User removed successfully");
        handleDeleteComplete();
      },
      () => {
        toastr.error("Error removing user");
        handleDeleteComplete();
      },
      true
    );
  };

  const CohortBatchVMRemove = (vmIds) => {
    props.getDataFromAPI(
      `/dashboard/api/v2/admin/vendingmachines/vm-cohorts/${id}/vendingmachines`,
      "DELETE",
      { vmIds: vmIds },
      () => {
        toastr.success(`${vmIds.length} Vending Machine(s) removed successfully`);
        handleDeleteComplete();
      },
      () => {
        toastr.error("Error removing VM(s)");
        handleDeleteComplete();
      },
      true
    );
  };

  const handleDeleteComplete = () => {
    setDeleting(false);
    setShowDeleteModal(false);
    setDeleteType(null);
    setDeleteIds([]);
    setDeleteMessage("");
    setShouldResetFields(true);
    LoadCohortViewDetails();
  };

  const handleResetFieldsComplete = () => {
    setShouldResetFields(false);
  };

  return (
    <CohortDetailsComponent
      loading={loading || deleting}
      cohortDetailsData={cohortDetailsData}
      showDeleteModal={showDeleteModal}
      setShowDeleteModal={setShowDeleteModal}
      handleDeleteClick={handleDeleteClick}
      handleVmDeleteClick={handleVmDeleteClick}
      confirmDelete={confirmDelete}
      deleteMessage={deleteMessage}
      deleting={deleting}
      shouldResetFields={shouldResetFields}
      onResetFieldsComplete={handleResetFieldsComplete}
      isDownloading={isDownloading}
      onDownload={handleDownloadVMList}
    />
  );
};

export default connect(
  () => ({}),
  { getDataFromAPI }
)(CohortViewContainer);